import {getTimeZoneByDateAndOffset} from './dateTimeLists';

/**
 * Check for a blank or invalid value
 *
 * @param term
 * @return {boolean}
 */
export const isBlank = (term) => {
    return (term === undefined || term === null || term === '');
};

/**
 * Sort array of objects alphanumerically
 *
 * @param arr
 * @param sortField
 * @param sortOrder
 */
export const sortArrayByKey = (arr, sortField, sortOrder = 'ASC') => {
	arr.sort(function (a, b) {
		const isNumerical = (!isNaN(a[sortField]) && !isNaN(b[sortField]));
		if (isNumerical) {
			return a[sortField] - b[sortField];

		} else {
			return a[sortField]
				.toString()
				.toLowerCase()
				.localeCompare(b[sortField].toString().toLowerCase());
		}
	});
	if (sortOrder === 'DESC') {
		arr = arr.reverse();
	}
	return arr;
};

/**
 * Sort array of objects by currency value
 *
 * @param arr
 * @param sortField
 * @param sortOrder
 * @returns {*}
 */
export const sortArrayByCurrencyKey = (arr, sortField, sortOrder = 'ASC') => {
	arr.sort(function (a, b) {
		let aVal = parseCurrency(a[sortField]);
		let bVal = parseCurrency(b[sortField]);
		return aVal - bVal;
	});
	if (sortOrder === 'DESC') {
		arr = arr.reverse();
	}
	return arr;
};

/**
 * Find an array item by key/value
 *
 * @param array
 * @param key
 * @param value
 * @return {*}
 */
export const getItemFromArray = (array, key, value) => {
    return array.find(a => a[key] === value);
};

/**
 * Sort date strings by format
 *
 * @param a
 * @param b
 * @param format
 * @param separator
 * @param sortOrder
 * @return {number}
 */
export const sortDate = (
    a,
    b,
    format = 'mm-dd-yyyy',
    separator = '-',
    sortOrder = 'ASC'
) => {
    let aArr = a.split(separator);
    let baArr = b.split(separator);
    let aComp, bComp;
    if (format === 'mm-dd-yyyy') {
        aComp = aArr[2] + separator + aArr[0] + separator + aArr[1];
        bComp = baArr[2] + separator + baArr[0] + separator + baArr[1];
        return sortOrder === 'DESC' ? bComp - aComp : aComp - bComp;
    }
    return sortOrder === 'DESC' ? b - a : a - b;
};

/**
 * Sort numbers
 *
 * @param a
 * @param b
 * @param sortOrder
 * @return {number}
 */
export const sortNumber = (a, b, sortOrder = 'ASC') => {
    return sortOrder === 'DESC' ? b - a : a - b;
};

/**
 * Sort array by object keys and values
 *
 * @param key
 * @param order
 * @return {Function}
 */
export const compareValues = (key, order = 'asc') => {
    return function (a, b) {
        if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) {
            return 0;
        }
        const varA = typeof a[key] === 'string' ? a[key].toUpperCase() : a[key];
        const varB = typeof b[key] === 'string' ? b[key].toUpperCase() : b[key];
        let comparison = 0;
        if (varA > varB) {
            comparison = 1;
        } else if (varA < varB) {
            comparison = -1;
        }
        return order === 'desc' ? comparison * -1 : comparison;
    };
};

/**
 * Update an object via Object.assign
 *
 * @param oldObject
 * @param newValues
 * @return {any}
 */
export const updateObject = (oldObject, newValues) => {
    return Object.assign({}, oldObject, newValues);
};

/**
 * Create a copy of an array of objects
 *
 * @param array
 * @return {*}
 */
export const copyObjectArray = array => {
    if (array && array.length > 0) {
        let copy = [];
        for (let i = 0; i < array.length; i++) {
            const item = JSON.parse(JSON.stringify(array[i]));
            copy.push(item);
        }
        return copy;
    }
    return array;
};

/**
 * Rearrange array to ensure two items are in a desired order
 *
 * @param arr
 * @param key1
 * @param key2
 * @param value1
 * @param value2
 * @returns {*}
 */
export const sequenceArrayItems = (arr, key1, key2, value1, value2) => {
    if (arr.length > 0) {
        const idx1 = arr.findIndex(r => r[key1] === value1);
        const idx2 = arr.findIndex(r => r[key2] === value2);
        if (idx1 !== -1 && idx2 !== -1 && idx2 < idx1) {
            const item2 = arr[idx2];
            arr.splice(idx2, 1);
            arr.splice(idx1, 0, item2);
        }
    }
    return arr;
};

export const getTodayOffsetHour = () => {
    const offsetMS = new Date().getTimezoneOffset();
    const hour = offsetMS / 60;
    const offset = addLeadingZero(hour);
    return '-' + offset + ':00';
};

/**
 * Convert offset string to a number
 *
 * @param offset
 * @returns {number}
 */
export const convertOffset = (offset) => {
    const arr = offset.split(':');
    return parseInt(arr[0], 10);
};

/**
 * Get standard time zone abbreviation
 *
 * @param date
 * @returns {string}
 */
export const getTimezoneAbbreviation = (date) => {
    const localeString = date.toLocaleDateString('en-US', {timeZoneName: 'short'});
    return localeString.split(' ')[1];
};

/**
 * Convert a date and offset to a local Date and standard time zone abbreviation
 *
 * @param date
 * @param offset
 * @returns {{localDate: Date, timezoneAbbreviation: *}}
 */
export const convertToTimezone = (date, offset) => {
    const ms = date.getTime() + (offset * 60 * 60 * 1000);
    const localDate = new Date(ms);
    const timezoneAbbreviation = getTimezoneAbbreviation(localDate);
    return {localDate, timezoneAbbreviation};
};

/**
 * Convert a datetime to a local Date
 *
 * @param datetime
 * @param offset
 * @returns {Date}
 */
export const convertToLocalDateByOffset = (datetime, offset) => {
    const date = new Date(datetime);
    const ms = date.getTime() + (offset * 60 * 60 * 1000);
    return new Date(ms);
};

/**
 * Convert date string to a formatted string
 *
 * @param datetime
 * @param format
 * @param includeTime
 * @param forceUTC
 * @param offset
 * @param includeTimezoneAbbreviation
 * @return {*}
 */
export const convertDate = (
    datetime,
    format,
    includeTime = true,
    forceUTC = false,
    offset = null,
    includeTimezoneAbbreviation = false
) => {
    const dateObj = new Date(datetime);

    if (offset) {
        let offsetArr = offset.split(':');
        let offsetHour = parseInt(offsetArr[0], 10);
        dateObj.setUTCHours(dateObj.getUTCHours() + offsetHour);
    }

    const mo = forceUTC ? dateObj.getUTCMonth() + 1 : dateObj.getMonth() + 1;
    const dy = forceUTC ? dateObj.getUTCDate() : dateObj.getDate();
    const yy = forceUTC ? dateObj.getUTCFullYear() : dateObj.getFullYear();
    let timeStr = '';
    if (includeTime) {
        let hr = forceUTC ? dateObj.getUTCHours() : dateObj.getHours();
        const ampm = hr > 11 ? 'pm' : 'am';
        if (hr === 0) {
            hr = 12;
        }
        if (hr > 12) {
            hr -= 12;
        }
        let mn = forceUTC ? dateObj.getUTCMinutes() : dateObj.getMinutes();
        let minVal = String('00' + mn).slice(-2);
        timeStr = ' ' + hr + ':' + minVal + ampm;
    }

    let dateStr;
    if (format === 'display') {
        // Format: mm/dd/yyyy hh:mm am
        dateStr = mo + '/' + dy + '/' + yy + timeStr;
    } else if (format === 'query') {
        // Format: yyyy-mm-dd
        let moVal = String('00' + mo).slice(-2);
        let dyVal = String('00' + dy).slice(-2);
        dateStr = yy + '-' + moVal + '-' + dyVal;
    } else if (format === 'query-with-time' && includeTime) {
        // Format: yyyy-mm-dd hh:mm:ss
        let moVal = String('00' + mo).slice(-2);
        let dyVal = String('00' + dy).slice(-2);
        dateStr = yy + '-' + moVal + '-' + dyVal + ' ' + timeStr;
    }
    if (includeTimezoneAbbreviation && offset) {
        const tzAbbr = getTimeZoneByDateAndOffset(datetime, offset);
        dateStr += ' ' + tzAbbr;

    }
    if (isNaN(yy) || isNaN(mo)) {
        dateStr = 'No Date';
    }
    return dateStr;
};


/**
 * Convert date string to a formatted 24-hour string
 *
 * @param datetime
 * @param forceUTC
 * @param offset
 * @returns {string}
 */
export const convertDateTo24HourString = (
    datetime,
    forceUTC = false,
    offset = null
) => {
    const dateObj = new Date(datetime);
    if (offset) {
        let offsetArr = offset.split(':');
        let offsetHour = parseInt(offsetArr[0], 10);
        dateObj.setUTCHours(dateObj.getUTCHours() + offsetHour);
    }
    // Format: YYYY/MM/DD HH:MM:SS
    const yy = forceUTC ? dateObj.getUTCFullYear() : dateObj.getFullYear();
    const mo = forceUTC ? dateObj.getUTCMonth() + 1 : dateObj.getMonth() + 1;
    let moVal = String('00' + mo).slice(-2);
    const dy = forceUTC ? dateObj.getUTCDate() : dateObj.getDate();
    let dyVal = String('00' + dy).slice(-2);
    let dateStr = yy + '/' + moVal + '/' + dyVal;

    let hr = forceUTC ? dateObj.getUTCHours() : dateObj.getHours();
    let hrVal = String('00' + hr).slice(-2);
    let mn = forceUTC ? dateObj.getUTCMinutes() : dateObj.getMinutes();
    let minVal = String('00' + mn).slice(-2);
    let ss = forceUTC ? dateObj.getUTCSeconds() : dateObj.getSeconds();
    let secVal = String('00' + ss).slice(-2);

    dateStr += ' ' + hrVal + ':' + minVal + ':' + secVal;
    if (isNaN(yy) || isNaN(mo)) {
        dateStr = 'No Date';
    }
    return dateStr;
};

export const convertTimeFromHoursMinutes = (hours, mins) => {
    let hr = parseInt(hours, 10);
    const ampm = hr > 11 ? 'pm' : 'am';
    if (hr === 0) {
        hr = 12;
    }
    if (hr > 12) {
        hr -= 12;
    }
    let minVal = String('00' + mins).slice(-2);
    return hr + ':' + minVal + ampm;
};

/**
 * Assemble a Date Range string for Report headers
 *
 * @param selectedStartDate
 * @param selectedEndDate
 * @param includeText
 * @param connector
 * @return {string}
 */
export const displayDateRangeString = (
    selectedStartDate,
    selectedEndDate,
    includeText = true,
    connector = 'through'
) => {
    const options = {year: 'numeric', month: 'long', day: 'numeric'};
    let startDate = new Date(selectedStartDate + 'T00:00:00');
    let endDate = new Date(selectedEndDate + 'T00:00:00');
    let startDateStr = startDate.toLocaleDateString('en-US', options);
    let endDateStr = endDate.toLocaleDateString('en-US', options);
    let text = includeText ? 'Date Range: ' : '';

    return text + startDateStr + ' ' + connector + ' ' + endDateStr;
};

/**
 * Assemble a Date Range from dates string
 *
 * @param selectedStartDate
 * @param selectedEndDate
 * @return {string}
 */
export const displayDateRangeStringFromDates = (
    selectedStartDate,
    selectedEndDate
) => {
    const options = {year: 'numeric', month: 'long', day: 'numeric'};
    let startDateStr = selectedStartDate.toLocaleDateString('en-US', options);
    let endDateStr = selectedEndDate.toLocaleDateString('en-US', options);

    return 'Date Range: ' + startDateStr + ' through ' + endDateStr;
};

/**
 * Convert Start and End Dates to range for Export filename
 *
 * @param startDate
 * @param endDate
 * @return {string}
 */
export const convertStartEndDatesForFile = (startDate, endDate) => {
    let from = new Date(startDate + 'T00:00:00');
    let to = new Date(endDate + 'T00:00:00');
    let fileStr = '';
    let yy = from.getFullYear();
    let mo = String('00' + parseInt(from.getMonth() + 1, 10)).slice(-2);
    let dd = String('00' + from.getDate()).slice(-2);
    fileStr += yy.toString() + mo.toString() + dd.toString() + '-';
    yy = to.getFullYear();
    mo = String('00' + parseInt(to.getMonth() + 1, 10)).slice(-2);
    dd = String('00' + to.getDate()).slice(-2);
    fileStr += yy.toString() + mo.toString() + dd.toString();
    return fileStr;
};

/**
 * Determine if a date is today or tomorrow
 *
 * @param period
 * @param date
 * @returns {boolean}
 */
export const dateIs = (period, date) => {
    const today = new Date();
    if (period === 'today') {
        return (
            date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear()
        );
    }
    if (period === 'tomorrow') {
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return (
            date.getDate() === tomorrow.getDate() &&
            date.getMonth() === tomorrow.getMonth() &&
            date.getFullYear() === tomorrow.getFullYear()
        );
    }
};

/**
 * Pad numbers less than 10 with a leading zero
 *
 * @param num
 * @return {string}
 */
export const addLeadingZero = num => {
    return String('00' + num).slice(-2);
};

/**
 * Convert value to number with two decimal places
 *
 * @param value
 * @param fromPennies
 * @return {string}
 */
export const toCurrency = (value, fromPennies = false) => {
    let valueToConvert = value;
    if (fromPennies) {
        valueToConvert = value * 0.01;
    }
    return addCommas(parseFloat(valueToConvert).toFixed(2));
};

/**
 * Add values from an array column
 *
 * @param list
 * @param col
 * @return {number}
 */
export const addFromColumn = (list, col) => {
    let sum = 0;
    for (let i = 0; i < list.length; i++) {
        const item = list[i][col].toString().replace(/,/g, '');
        sum += parseFloat(item);
    }
    return sum;
};

/**
 * Average values from an array column
 *
 * @param list
 * @param col
 * @returns {number}
 */
export const averageColumn = (list, col) => {
    let amounts = [];
    for (let i = 0; i < list.length; i++) {
        const item = list[i][col].toString().replace(/,/g, '');
        const avg = parseFloat(item);
        if (avg > 0) {
            amounts.push(parseFloat(item));
        }
    }
    if (amounts.length > 0) {
        return amounts.reduce((a, b) => a + b) / amounts.length;
    }
    return 0;
};

/**
 * Convert formatted value back to number
 *
 * @param value
 * @return {number}
 */
export const parseCurrency = value => {
    const item = value.toString().replace(/,/g, '');
    return parseFloat(item);
};

/**
 * Sort currency values
 *
 * @param a
 * @param b
 * @param desc
 * @return {number}
 */
export const sortCurrency = (a, b, desc = false) => {
    a = a === null || a === undefined ? -Infinity : a;
    b = b === null || b === undefined ? -Infinity : b;
    let aStr = a.toString().replace(/,/g, '');
    let aVal = parseFloat(aStr);
    let bStr = b.toString().replace(/,/g, '');
    let bVal = parseFloat(bStr);
    if (aVal > bVal) {
        return 1;
    }
    if (aVal < bVal) {
        return -1;
    }
    return 0;
};

/**
 * Convert percent value to number
 *
 * @param value
 * @return {number}
 */
export const parsePercent = value => {
    const item = value.toString().replace(/%/g, '');
    return parseFloat(item);
};

/**
 * Add commas to numerical strings
 *
 * @param nStr
 * @return {string}
 */
export const addCommas = nStr => {
    nStr += '';
    let x = nStr.split('.');
    let x1 = x[0];
    let x2 = x.length > 1 ? '.' + x[1] : '';
    let rgx = /(\d+)(\d{3})/;
    while (rgx.test(x1)) {
        x1 = x1.replace(rgx, '$1,$2');
    }
    return x1 + x2;
};

/**
 * Create a human-readable percentage string from a percent value
 *
 * @param percent
 * @return {string}
 */
export const createPercentageString = percent => {
    let percentStr;
    if (percent === 0) {
        percentStr = '0%';
    } else if (percent < 0.1) {
        percentStr = percent.toFixed(3) + '%';
    } else {
        percentStr = percent.toFixed(2) + '%';
    }
    return percentStr;
};

/**
 * Convert bytes to human-readable string
 *
 * @param bytes
 * @returns {string}
 */
export const convertBytesToString = (bytes) => {
    let i = Math.floor(Math.log(bytes) / Math.log(1024)), sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    return (bytes / Math.pow(1024, i)).toFixed(2) * 1 + ' ' + sizes[i];
};

/**
 * Validate each email in an array for form submission
 *
 * @param value
 * @returns {undefined}
 */
export const validateEmailArray = (value) => {
    let error = undefined;
    if (value && value !== '') {
        const arr = value.split(',');
        for (let email of arr) {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
                error = email + ' is not a valid email address.';
                break;
            }
        }
    }
    return error;
};

/**
 * Get scrollbar width (for browsers and table spacing)
 *
 * @returns {number}
 */
export const getScrollbarWidth = () => {
    return window.innerWidth - document.documentElement.clientWidth;
};



// WEBPACK FOOTER //
// ./src/Fuse/shared/functions.js